<template>
  <div id="generated-travel-book" class="h-100">
    <v-loader v-if="loadingTravelBook" />

    <div v-else-if="show404Error" class="error-container d-flex align-items-center justify-content-center h-100">
      <div class="p-16 py-lg-24 px-lg-32">
        <div class="title fw-bold">{{ $t("general.errors.error404") }}</div>
        <div class="text-xl mb-16">{{ $t("general.errors.pageNotFound") }}</div>
        <div>{{ $t("general.errors.pageNotFoundText") }}</div>
      </div>
    </div>

    <template v-else>
      <v-travel-book-page-header v-if="!!tour" :tour="tour" />

      <table>
        <thead>
          <tr>
            <td>
              <div class="header-space" />
            </td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              <div class="page">
                <div class="page-inner container">
                  <div class="d-flex justify-content-between align-items-center mb-32">
                    <h3 class="h4 mb-0">
                      {{ tour.name }}
                    </h3>

                    <v-button
                      :disabled="downloadingPdf"
                      class="fw-medium text-sm hide-print"
                      size="xs"
                      variant="primary"
                      @click="downloadPDF"
                    >
                      <v-icon icon="download" space="me-8" />

                      <template v-if="downloadingPdf">{{ $t("general.button.generatingPdf") }}</template>
                      <template v-else>{{ $t("general.button.download") }}</template>
                    </v-button>
                  </div>

                  <the-travel-book-resume-contact v-if="!!tour" :tour="tour" />

                  <div v-if="!!featuredImage" class="featured-image mt-32">
                    <v-picture class="d-block">
                      <img :alt="$t('clientTour.featuredImage')" :src="featuredImage.storageUrl" />
                    </v-picture>
                  </div>
                </div>
              </div>

              <div class="page">
                <the-travel-book-resume-days
                  v-if="!!tour"
                  :is-summary="false"
                  :locations="locations"
                  :tour-days="tourDays"
                  :client-tour="tour?.clientTour"
                  :tour-template="tourTemplate"
                />
              </div>

              <div class="page">
                <the-travel-book-days
                  :locations="locations"
                  :tour="tour"
                  :tour-days="tourDays"
                  :tour-template="tourTemplate"
                />
              </div>
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td>
              <div class="footer-space" />
            </td>
          </tr>
        </tfoot>
      </table>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import axios from "axios";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import Tour from "@/api/objects/Tour";
import TheTravelBookResumeContact from "@/components/tour/book/parts/TheTravelBookResumeContact.vue";
import VTravelBookPageHeader from "@/components/tour/book/parts/VTravelBookPageHeader.vue";
import TheTravelBookResumeDays from "@/components/tour/book/parts/TheTravelBookResumeDays.vue";
import TourDay from "@/api/objects/TourDay";
import TheTravelBookDays from "@/components/tour/book/parts/TheTravelBookDays.vue";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Media from "@/api/objects/Media";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  caseId: string;
  tourId: string;
}>();

const route = useRoute();

const token = computed(() => route.query.token);
const locale = computed(() => route.query.locale);
const summary = computed(() => (!!route.query.summary && route.query.summary === "1" ? 1 : 0));
const domain = computed(() => import.meta.env.VITE_APP_API_URL);

const loadingTravelBook = ref(true);
const downloadingPdf = ref(false);
const show404Error = ref(false);

const tour = ref<Tour>({} as Tour);
const tourDays = ref<TourDay[]>([]);
const locations = ref<Location[]>([]);
const featuredImage = ref<Media | null>(null);
const tourTemplate = ref<any[]>([]);

onMounted(async () => {
  if (!!token.value) {
    const params = {
      token: token.value,
      locale: locale.value,
      summary: summary.value,
    };

    try {
      tour.value = await api.travelBook.simple(Number(route.params.caseId), Number(route.params.tourId), params);

      locations.value = await api.travelBook.locations(
        Number(route.params.caseId),
        Number(route.params.tourId),
        params
      );
      const tourData = await api.travelBook.dataForPreview(
        Number(route.params.caseId),
        Number(route.params.tourId),
        params
      );

      tourTemplate.value = tourData.clientTour;
      featuredImage.value = tourData.featuredImage;
      tourDays.value = tourData.tourDays;

      loadingTravelBook.value = false;
    } catch (e: any) {
      loadingTravelBook.value = false;
      show404Error.value = true;
    }
  } else {
    show404Error.value = true;
  }
});

const downloadPDF = async () => {
  try {
    downloadingPdf.value = true;

    const params = {
      token: token.value,
      locale: locale.value,
      summary: summary.value,
    };

    const response = await axios({
      url: `/api/case/${props.caseId}/tour/${props.tourId}/preview/download`,
      baseURL: domain.value,
      params,
      responseType: "blob",
      withCredentials: true,
    });

    const contentDisposition = response.headers["content-disposition"];
    let filename = tour.value.name || "tour";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `travel-book-${filename}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    const toast = (window as any).$toast;
    const t = (window as any).$t;

    toast?.error?.(t?.("travelBook.downloadErrorText") || "Failed to download PDF");
  } finally {
    downloadingPdf.value = false;
  }
};
</script>

<style lang="scss" scoped>
.hide-print {
  @media print {
    display: none;
  }
}

#generated-travel-book {
  .error-container {
    .title {
      font-size: 5rem;
    }
  }

  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  table {
    width: 100%;
  }

  .header-space {
    position: relative;
    z-index: 1;

    @media print {
      height: 7.375rem;
    }
  }

  .footer-space {
    position: relative;
    z-index: 1;
  }

  @page {
    size: A4;
    margin: 1.5cm;
  }

  @media print {
    thead {
      display: table-header-group;
    }

    tfoot {
      display: table-footer-group;
    }
  }

  .page {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100vw;

    @media print {
      page-break-before: always;
    }

    :deep(.page-inner) {
      padding-inline: 2rem;
      margin: 0 auto;
      width: 100%;

      @media only screen {
        padding-block: 2rem;
      }

      @media print {
        max-width: 58.125rem !important;
        padding-block: 1rem;
      }
    }
  }

  .featured-image {
    :deep(img) {
      height: 100%;
      width: 100%;
      max-height: 18rem;
      object-fit: contain;
    }
  }
}
</style>
